// src/components/shell/LeadsModeBanners.tsx
// Leads-mode-specific banners that used to live in LeadWorkspaceHeader*.
// Composed by WorkspaceModeBanners only when `mode === "leads"`.
//
// Slice A of docs/planning/activity_dock_workflow_facilitation.md §6:
// the Location Links collapsible card previously rendered here has been
// lifted into the sidebar (`SidebarLocationLinks`) so it sits alongside
// the rest of the global-nav cluster rather than wedged into the
// Leads-mode header. The `showLocationLinks` / `setShowLocationLinks`
// open-state plumbing — and the tool-link / org-settings hooks that
// fed the card — went with it.
//
// Slice B of the same doc: the `Status: …` paragraph retired entirely.
// The only piece of its content that was actually load-bearing for
// users was the "Select your location to continue" guidance, which is
// now an explicit role-gated banner — only visible to users with the
// `sales` role and an associated salesperson (i.e. users whose
// workflow actually requires a per-location context).
//
// Slice D of the same doc: the `totalUnsent` / `todaysUnsent` /
// `todaysSent` badge row is gone — that information now lives in the
// activity dock's `ActivityDockUnsentLeadsSection` plus the dock-tab
// count badge surfaced by `deriveActivityDockStatus`. The dock follows
// the user across modes, so the badge is visible everywhere a sales
// user works, not just at the top of Leads mode.
//
// Slice F (this commit): the remaining `lastSendAt` / `queuedCount`
// banners are sales-role-gated. Both are workflow notifications that
// only apply to users who send leads to GHL — owner-only /
// platform-only / trainer-only users who land in Leads mode (it's
// `null`-gated in TAB_ACCESS so anyone can navigate to it) no longer
// see leftover workflow chrome that doesn't belong to their role.
// The `status` write-only state and every `setStatus(...)` callsite
// retired completely in the same commit; no more side-effect string
// channel.
import { useAuth } from "@/contexts/AuthContext";
import { ProfileEditor } from "@/components/lead/sections/ProfileEditor";
import type { OrgLocation } from "@/config/locations";
import type { Location } from "@/types/location";

type Salesperson = { name: string; cell: string; email: string };

export type LeadsModeBannersProps = {
  location: Location | null;
  setLocation: (l: Location) => void;
  lastSendAt: number | null;
  lastSendStatus: "success" | "error" | null;
  queuedCount: number;
  salesperson: Salesperson | null;
  setSalesperson: (s: Salesperson | null) => void;
  showProfileEditor: boolean;
  locationsLoaded: boolean;
  locations: OrgLocation[];
  refreshLocations: (reason?: string, opts?: { force?: boolean }) => void;
  voiceEnabled: boolean;
  setVoiceEnabled: (v: boolean) => void;
  voiceProvider: "system" | "custom";
  setVoiceProvider: (v: "system" | "custom") => void;
  speakAnswers: boolean;
  canSpeakHelp: boolean;
  setSpeakMode: (m: "answers" | "help" | "all") => void;
};

export function LeadsModeBanners(props: LeadsModeBannersProps) {
  const {
    location,
    setLocation,
    lastSendAt,
    lastSendStatus,
    queuedCount,
    salesperson,
    setSalesperson,
    showProfileEditor,
    locationsLoaded,
    locations,
    refreshLocations,
    voiceEnabled,
    setVoiceEnabled,
    voiceProvider,
    setVoiceProvider,
    speakAnswers,
    canSpeakHelp,
    setSpeakMode,
  } = props;

  const auth = useAuth();
  // Sales-role gate for the send-related banners (Slice F audit). The
  // location-guidance banner has its own narrower gate (also requires
  // `salesperson`); these only need the role check because both can
  // surface mid-session as the user interacts with the workflow.
  const showSendBanners = auth.hasRole("sales");
  // Role-gated location-guidance banner. Replaces the bootstrap's
  // `setStatus("Select your location to continue")` /
  // `setStatus("No location assigned to your account yet…")` strings
  // for the only audience those strings ever applied to: a sales-role
  // user with an associated salesperson record who hasn't selected a
  // location yet. Owner / platform / trainer / admin users without
  // sales no longer see workflow-prompt copy that's irrelevant to
  // their role. `salesperson` is required because no salesperson →
  // the bootstrap was previously showing "Loading your profile…",
  // which is transient enough to silently drop.
  const showLocationGuidance =
    !location && !!salesperson && auth.hasRole("sales");
  const locationGuidance: { primary: string; secondary: string } | null = showLocationGuidance
    ? locations.length === 0
      ? {
          primary: "No location assigned to your account yet.",
          secondary: "Ask an admin to assign your location before sending leads.",
        }
      : {
          primary: "Select your location to continue.",
          secondary: "Choose your active location in Settings → Profile.",
        }
    : null;

  return (
    <>
      {locationGuidance ? (
        <div
          className="mb-4 text-left text-sm rounded-2xl px-4 py-3 border bg-amber-50 border-amber-200 text-amber-900"
          role="status"
        >
          <p className="font-semibold">{locationGuidance.primary}</p>
          <p className="mt-0.5 text-xs text-amber-800">{locationGuidance.secondary}</p>
        </div>
      ) : null}

      {showSendBanners && lastSendAt && lastSendStatus ? (
        <div
          className={
            "mb-4 text-sm rounded-2xl px-4 py-3 text-left border " +
            (lastSendStatus === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800")
          }
        >
          {lastSendStatus === "success" ? (
            <p>
              Last lead <strong>sent to CRM</strong> at{" "}
              {new Date(lastSendAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.
            </p>
          ) : (
            <p>
              Last send <strong>failed</strong>. The lead is saved locally and will retry when you&apos;re back online.
            </p>
          )}
        </div>
      ) : null}

      {showSendBanners && queuedCount > 0 ? (
        <div className="mb-4 text-left text-xs md:text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2">
          {queuedCount === 1
            ? "1 lead is waiting to sync to your CRM (will send when you’re back online)."
            : `${queuedCount} leads are waiting to sync to your CRM (will send when you’re back online).`}
        </div>
      ) : null}

      {salesperson && showProfileEditor ? (
        <ProfileEditor
          salesperson={salesperson}
          setSalesperson={setSalesperson}
          location={location}
          setLocation={setLocation}
          locationsLoaded={locationsLoaded}
          locations={locations}
          refreshLocations={refreshLocations}
          voiceEnabled={voiceEnabled}
          setVoiceEnabled={setVoiceEnabled}
          voiceProvider={voiceProvider}
          setVoiceProvider={setVoiceProvider}
          speakAnswers={speakAnswers}
          canSpeakHelp={canSpeakHelp}
          setSpeakMode={setSpeakMode}
        />
      ) : null}
    </>
  );
}
